"use client";

import { m } from "framer-motion";
import AnimatedSection from "./AnimatedSection";
import SectionHeader from "./SectionHeader";

const SERVICES = [
  {
    title: "Web apps that hold up",
    description:
      "Hand-built with Next.js, TypeScript, and Tailwind because they let me ship fast without making future-me hate present-me. No framework-for-the-sake-of-it. The simplest thing that holds the load wins.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-8 w-8">
        <path
          d="M3 8L10.89 13.26C11.57 13.7 12.43 13.7 13.11 13.26L21 8M5 19H19C20.1 19 21 18.1 21 17V7C21 5.9 20.1 5 19 5H5C3.9 5 3 5.9 3 7V17C3 18.1 3.9 19 5 19Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    gradient: "from-emerald-500/20 to-emerald-500/5",
    iconColor: "text-emerald-400",
  },
  {
    title: "Mobile that doesn't feel like a WebView",
    description:
      "iOS and Android from one Capacitor or React Native codebase — the kind of native feel that comes from caring about gesture timing and frame budgets, not just shipping the shell. I've eaten Apple's rejection letters. I know what they want.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-8 w-8">
        <path
          d="M12 18H12.01M7 4H17C17.55 4 18 4.45 18 5V19C18 19.55 17.55 20 17 20H7C6.45 20 6 19.55 6 19V5C6 4.45 6.45 4 7 4Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    gradient: "from-cyan-500/20 to-cyan-500/5",
    iconColor: "text-cyan-400",
  },
  {
    title: "Stores that take money cleanly",
    description:
      "Stripe Connect, subscriptions, marketplace splits — the boring parts that have to work. If your checkout breaks at 3am, I find that.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-8 w-8">
        <path
          d="M6 2L3 6V20C3 20.55 3.45 21 4 21H20C20.55 21 21 20.55 21 20V6L18 2H6ZM3 6H21M16 10C16 12.21 14.21 14 12 14C9.79 14 8 12.21 8 10"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    gradient: "from-amber-500/20 to-amber-500/5",
    iconColor: "text-amber-400",
  },
  {
    title: "Brand that's actually yours",
    description:
      "Identity systems that survive past the launch deck. The brand doesn't fall apart when you ship a feature it didn't account for — because the same person built both. Not template-shop. Not committee design. Yours.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-8 w-8">
        <path
          d="M12 2L2 7L12 12L22 7L12 2ZM2 17L12 22L22 17M2 12L12 17L22 12"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    gradient: "from-violet-500/20 to-violet-500/5",
    iconColor: "text-violet-400",
  },
];

const Services = () => {
  return (
    <section id="services" className="relative px-4 py-24">
      <div className="mx-auto max-w-6xl">
        {/* Section header — workshop framing replaces the templated
            "What I Do / Engineering Digital Experiences" copy. The
            opinionated subheading is the brand voice doing work: it
            signals one builder, not an agency, and rejects the
            relay-race-between-teams model most shops sell. */}
        <SectionHeader
          index="01"
          code="services"
          align="left"
          title={<>Four trades, <span className="gradient-text">one workshop</span></>}
          blurb="I do all of them so the seams between them never show — the person writing the code is the one choosing the typography, and the same hand answering support is the one shipping the next feature."
        />

        {/* Service cards */}
        <div className="grid gap-6 md:grid-cols-2">
          {SERVICES.map((service, index) => (
            <AnimatedSection key={service.title} delay={index * 0.1}>
              <m.div
                whileHover={{ scale: 1.02 }}
                className="glass-card group relative overflow-hidden rounded-2xl p-8"
              >
                {/* Gradient bg on hover */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 transition-opacity group-hover:opacity-100`}
                />

                <div className="relative z-10">
                  <div
                    className={`mb-4 inline-flex rounded-xl bg-white/5 p-3 ${service.iconColor}`}
                  >
                    {service.icon}
                  </div>
                  <h3 className="font-[family-name:var(--font-space-grotesk)] text-xl font-semibold text-white">
                    {service.title}
                  </h3>
                  <p className="mt-2 text-[#94A3B8] leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </m.div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
